import React, { useState, useEffect, useCallback } from 'react';
import { Search, Eye, Pencil, Trash2, Plus, Upload, Package, Check, X as XIcon, Star, RefreshCw, ArrowUpDown, ArrowUp, ArrowDown, AlertTriangle } from 'lucide-react';
import { getAdminBulkLots, createBulkLot, updateBulkLot, deleteBulkLot, backfillBulkLotGrades, backfillBulkLotPricing, quickImportAnalyze, quickImportExecute, deleteAllBulkLots } from '../../lib/api';
import { Toaster, toast } from 'sonner';
import { DangerConfirmModal } from '../../components/DangerConfirmModal';

const formatDate = (iso) => {
  if (!iso) return '\u2014';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatPrice = (p) => p ? `$${Number(p).toLocaleString('en-US', { minimumFractionDigits: 0 })}` : '\u2014';

const STATUS = {
  active: { label: 'Active', color: 'bg-[#10B981]/20 text-[#10B981]' },
  inactive: { label: 'Inactive', color: 'bg-white/10 text-white/50' },
  sold: { label: 'Sold', color: 'bg-[#EF4444]/20 text-[#EF4444]' },
};

export const AdminBulkLots = () => {
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [editingLot, setEditingLot] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [backfilling, setBackfilling] = useState(false);
  const [repairingPricing, setRepairingPricing] = useState(false);
  const [addingLot, setAddingLot] = useState(false);
  const [addForm, setAddForm] = useState({});
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortHeader = ({ field, children, align }) => {
    const active = sortField === field;
    return (
      <th
        className={`px-3 py-3 ${align || 'text-left'} cursor-pointer select-none hover:text-[#00D4FF] transition-colors ${active ? 'text-[#00D4FF]' : ''}`}
        onClick={() => toggleSort(field)}
        data-testid={`sort-${field}`}
      >
        <span className="inline-flex items-center gap-1">
          {children}
          {active ? (sortDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-30" />}
        </span>
      </th>
    );
  };

  const sortedLots = [...lots].sort((a, b) => {
    let aVal = a[sortField], bVal = b[sortField];
    if (aVal == null) aVal = '';
    if (bVal == null) bVal = '';
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    }
    const aStr = String(aVal).toLowerCase(), bStr = String(bVal).toLowerCase();
    if (aStr < bStr) return sortDir === 'asc' ? -1 : 1;
    if (aStr > bStr) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const fetchLots = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;
      const res = await getAdminBulkLots(params);
      setLots(res.data);
    } catch { toast.error('Failed to load lots'); }
    finally { setLoading(false); }
  }, [statusFilter, search]);

  useEffect(() => { fetchLots(); }, [fetchLots]);

  const handleStatusToggle = async (lot, newStatus) => {
    try {
      await updateBulkLot(lot.id, { status: newStatus });
      toast.success(`Lot marked as ${newStatus}`);
      fetchLots();
    } catch { toast.error('Failed to update status'); }
  };

  const handleToggleFeatured = async (lot) => {
    try {
      await updateBulkLot(lot.id, { is_featured: !lot.is_featured });
      toast.success(lot.is_featured ? 'Removed from featured' : 'Added to featured');
      fetchLots();
    } catch { toast.error('Failed to update'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this lot?')) return;
    try {
      await deleteBulkLot(id);
      toast.success('Lot deleted');
      fetchLots();
    } catch { toast.error('Failed to delete'); }
  };

  const openAddForm = () => {
    setAddForm({ title: '', brand: '', category: '', subcategory: '', supplier_reference: '',
      quantity: '', grade: '', supplier_cost: '', markup_percent: '', lot_price: '', avg_unit_price: '',
      cpu: '', ram: '', storage: '', screen_size: '', status: 'active', description: '', condition_notes: '' });
    setAddingLot(true);
  };

  const recalcAdd = (updates) => {
    const next = { ...addForm, ...updates };
    const cost = parseFloat(next.supplier_cost) || 0;
    const markup = parseFloat(next.markup_percent) || 0;
    const qty = parseInt(next.quantity) || 1;
    if (cost > 0 && markup >= 0) {
      next.lot_price = Math.round(cost * (1 + markup / 100) * 100) / 100;
      next.avg_unit_price = Math.round((next.lot_price / qty) * 100) / 100;
    }
    setAddForm(next);
  };

  const handleCreate = async () => {
    if (!addForm.title?.trim()) { toast.error('Title is required'); return; }
    try {
      await createBulkLot({
        ...addForm,
        quantity: parseInt(addForm.quantity) || 0,
        supplier_cost: parseFloat(addForm.supplier_cost) || 0,
        markup_percent: parseFloat(addForm.markup_percent) || 0,
        lot_price: parseFloat(addForm.lot_price) || 0,
        avg_unit_price: parseFloat(addForm.avg_unit_price) || 0,
      });
      toast.success('Lot created');
      setAddingLot(false);
      fetchLots();
    } catch { toast.error('Failed to create lot'); }
  };

  // Quick Import state
  const [quickImportOpen, setQuickImportOpen] = useState(false);
  const [quickImportStep, setQuickImportStep] = useState('upload'); // upload | preview | importing
  const [quickImportData, setQuickImportData] = useState(null);
  const [quickUploading, setQuickUploading] = useState(false);

  // Delete All state
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const [deleteAllLoading, setDeleteAllLoading] = useState(false);

  const handleQuickUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setQuickUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await quickImportAnalyze(formData);
      setQuickImportData(res.data);
      setQuickImportStep('preview');
    } catch (err) {
      toast.error('Failed to parse file');
    } finally {
      setQuickUploading(false);
    }
  };

  const handleQuickExecute = async () => {
    if (!quickImportData) return;
    setQuickImportStep('importing');
    try {
      // Feb-2026 — flat markup input removed. The backend now uses
      // the shared Bulk Lot pricing function for every row.
      const res = await quickImportExecute({
        session_id: quickImportData.session_id,
      });
      toast.success(`Imported ${res.data.created} lots (replaced ${res.data.deleted} old)`);
      setQuickImportOpen(false);
      setQuickImportStep('upload');
      setQuickImportData(null);
      fetchLots();
    } catch {
      toast.error('Import failed');
      setQuickImportStep('preview');
    }
  };

  const resetQuickImport = () => {
    setQuickImportOpen(false);
    setQuickImportStep('upload');
    setQuickImportData(null);
  };

  const openEdit = (lot) => {
    setEditingLot(lot.id);
    setEditForm({
      title: lot.title, brand: lot.brand, category: lot.category, subcategory: lot.subcategory,
      quantity: lot.quantity, grade: lot.grade,
      supplier_cost: lot.supplier_cost || 0, markup_percent: lot.markup_percent || 0,
      lot_price: lot.lot_price, avg_unit_price: lot.avg_unit_price,
      cpu: lot.cpu, ram: lot.ram, storage: lot.storage, screen_size: lot.screen_size,
      description: lot.description, condition_notes: lot.condition_notes,
      supplier_reference: lot.supplier_reference, status: lot.status || 'active',
    });
  };

  const recalcPricing = (updates) => {
    const next = { ...editForm, ...updates };
    const cost = parseFloat(next.supplier_cost) || 0;
    const markup = parseFloat(next.markup_percent) || 0;
    const qty = parseInt(next.quantity) || 1;
    if (cost > 0 && markup >= 0) {
      const salePrice = Math.round(cost * (1 + markup / 100) * 100) / 100;
      next.lot_price = salePrice;
      next.avg_unit_price = Math.round((salePrice / qty) * 100) / 100;
    }
    setEditForm(next);
  };

  const saveEdit = async () => {
    try {
      const data = {
        ...editForm,
        quantity: parseInt(editForm.quantity) || 0,
        supplier_cost: parseFloat(editForm.supplier_cost) || 0,
        markup_percent: parseFloat(editForm.markup_percent) || 0,
        lot_price: parseFloat(editForm.lot_price) || 0,
        avg_unit_price: parseFloat(editForm.avg_unit_price) || 0,
      };
      await updateBulkLot(editingLot, data);
      toast.success('Lot updated');
      setEditingLot(null);
      fetchLots();
    } catch { toast.error('Failed to update'); }
  };

  const handleBackfill = async () => {
    setBackfilling(true);
    try {
      const res = await backfillBulkLotGrades();
      toast.success(`Updated ${res.data.updated} lots with grade data`);
      fetchLots();
    } catch { toast.error('Backfill failed'); }
    finally { setBackfilling(false); }
  };

  const handleRepairPricing = async () => {
    if (!window.confirm('Run one-time pricing repair? This will backfill supplier_cost and markup_percent for lots that have a sale price but missing cost data.')) return;
    setRepairingPricing(true);
    try {
      const res = await backfillBulkLotPricing();
      const s = res.data.summary;
      toast.success(`Repaired ${s.successfully_backfilled} lots. ${s.skipped_for_manual_review} need manual review.`);
      if (res.data.skipped_lots?.length > 0) {
        console.table(res.data.skipped_lots);
      }
      fetchLots();
    } catch { toast.error('Pricing repair failed'); }
    finally { setRepairingPricing(false); }
  };

  const counts = { all: lots.length, active: lots.filter(l => l.status === 'active').length, sold: lots.filter(l => l.status === 'sold').length };

  return (
    <div className="p-6" data-testid="admin-bulk-lots-page">
      <Toaster position="top-right" theme="dark" />

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" data-testid="bulk-lots-title">Bulk Lots</h1>
          <p className="text-white/50 text-sm mt-1">{counts.active} active, {counts.sold} sold, {counts.all} total</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={handleRepairPricing} disabled={repairingPricing}
            className="flex items-center gap-2 px-4 py-2.5 bg-white/5 border border-white/10 text-white/60 text-sm hover:text-white hover:border-white/20 transition-colors disabled:opacity-40"
            data-testid="repair-pricing-btn">
            <RefreshCw className={`w-4 h-4 ${repairingPricing ? 'animate-spin' : ''}`} /> {repairingPricing ? 'Repairing...' : 'Repair Pricing'}
          </button>
          <button onClick={handleBackfill} disabled={backfilling}
            className="flex items-center gap-2 px-4 py-2.5 bg-white/5 border border-white/10 text-white/60 text-sm hover:text-white hover:border-white/20 transition-colors disabled:opacity-40"
            data-testid="backfill-grades-btn">
            <RefreshCw className={`w-4 h-4 ${backfilling ? 'animate-spin' : ''}`} /> {backfilling ? 'Computing...' : 'Auto-Fill Grades'}
          </button>
          <button onClick={() => { resetQuickImport(); setQuickImportOpen(true); }} className="flex items-center gap-2 px-4 py-2.5 bg-[#F59E0B]/10 border border-[#F59E0B]/30 text-[#F59E0B] text-sm font-medium hover:bg-[#F59E0B]/20 transition-colors" data-testid="quick-import-btn">
            <Upload className="w-4 h-4" /> Quick Import
          </button>
          {counts.all > 0 && (
            <button onClick={() => setDeleteAllOpen(true)} className="flex items-center gap-2 px-3 py-2.5 bg-red-500/10 border border-red-500/20 text-red-400 text-sm hover:bg-red-500/20 transition-colors" data-testid="delete-all-bulk-lots-btn">
              <AlertTriangle className="w-4 h-4" /> Delete All
            </button>
          )}
          <button onClick={openAddForm} className="flex items-center gap-2 px-4 py-2.5 bg-[#10B981]/10 border border-[#10B981]/30 text-[#10B981] text-sm font-medium hover:bg-[#10B981]/20 transition-colors" data-testid="add-lot-btn">
            <Plus className="w-4 h-4" /> Add Lot
          </button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search lots..." className="w-full pl-10 pr-4 py-2.5 bg-[#0A0A0A] border border-white/10 text-white text-sm" data-testid="lots-search" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-3 py-2.5 bg-[#0A0A0A] border border-white/10 text-white text-sm" data-testid="lots-status-filter">
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="sold">Sold</option>
        </select>
      </div>

      {loading ? (
        <div className="text-center py-16 text-white/40">Loading...</div>
      ) : lots.length === 0 ? (
        <div className="text-center py-16" data-testid="no-lots">
          <Package className="w-12 h-12 mx-auto mb-4 text-white/20" />
          <p className="text-white/40 mb-4">No bulk lots found</p>
          <button onClick={() => { resetQuickImport(); setQuickImportOpen(true); }} className="btn-primary text-sm">Import Your First Lots</button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="bulk-lots-table">
            <thead>
              <tr className="border-b border-white/10 text-white/50 text-xs uppercase tracking-wider">
                <SortHeader field="created_at">Date</SortHeader>
                <SortHeader field="title">Lot Title</SortHeader>
                <SortHeader field="supplier_reference">Lot #</SortHeader>
                <SortHeader field="brand">Brand</SortHeader>
                <SortHeader field="category">Cat</SortHeader>
                <SortHeader field="quantity" align="text-right">Qty</SortHeader>
                <SortHeader field="grade">Grade</SortHeader>
                <SortHeader field="supplier_cost" align="text-right">Cost</SortHeader>
                <SortHeader field="markup_percent" align="text-right">Markup</SortHeader>
                <SortHeader field="lot_price" align="text-right">Sale Price</SortHeader>
                <SortHeader field="status">Status</SortHeader>
                <th className="px-3 py-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedLots.map(lot => (
                <tr key={lot.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`lot-row-${lot.id}`}>
                  <td className="px-3 py-3 text-white/50 text-xs whitespace-nowrap">{formatDate(lot.created_at)}</td>
                  <td className="px-3 py-3 text-white font-medium max-w-[250px] truncate">
                    {lot.is_new && <span className="text-[10px] px-1.5 py-0.5 bg-[#00D4FF]/20 text-[#00D4FF] mr-2">NEW</span>}
                    {lot.title}
                  </td>
                  <td className="px-3 py-3 text-[#00D4FF] font-mono text-xs">{lot.supplier_reference || '\u2014'}</td>
                  <td className="px-3 py-3 text-white/70">{lot.brand || '\u2014'}</td>
                  <td className="px-3 py-3 text-white/70">{lot.category || '\u2014'}</td>
                  <td className="px-3 py-3 text-white/70 text-right font-mono">{lot.quantity}</td>
                  <td className="px-3 py-3 text-white/70">{lot.grade || '\u2014'}</td>
                  <td className="px-3 py-3 text-white/40 text-right font-mono text-xs">{formatPrice(lot.supplier_cost)}</td>
                  <td className="px-3 py-3 text-white/40 text-right font-mono text-xs">{lot.markup_percent ? `${lot.markup_percent}%` : '\u2014'}</td>
                  <td className="px-3 py-3 text-[#10B981] text-right font-mono font-medium">{formatPrice(lot.lot_price)}</td>
                  <td className="px-3 py-3">
                    <span className={`px-2 py-1 text-xs font-medium ${(STATUS[lot.status] || STATUS.active).color}`}>{(STATUS[lot.status] || STATUS.active).label}</span>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => handleToggleFeatured(lot)} className={`p-1.5 transition-colors ${lot.is_featured ? 'text-[#F59E0B]' : 'text-white/20 hover:text-[#F59E0B]'}`} title="Featured"><Star className="w-4 h-4" /></button>
                      <button onClick={() => openEdit(lot)} className="p-1.5 text-white/40 hover:text-[#00D4FF]" title="Edit"><Pencil className="w-4 h-4" /></button>
                      {lot.status === 'active' ? (
                        <button onClick={() => handleStatusToggle(lot, 'sold')} className="p-1.5 text-white/40 hover:text-[#EF4444]" title="Mark Sold"><Check className="w-4 h-4" /></button>
                      ) : lot.status === 'sold' ? (
                        <button onClick={() => handleStatusToggle(lot, 'active')} className="p-1.5 text-white/40 hover:text-[#10B981]" title="Reactivate"><Package className="w-4 h-4" /></button>
                      ) : (
                        <button onClick={() => handleStatusToggle(lot, 'active')} className="p-1.5 text-white/40 hover:text-[#10B981]" title="Activate"><Check className="w-4 h-4" /></button>
                      )}
                      <button onClick={() => handleDelete(lot.id)} className="p-1.5 text-white/40 hover:text-[#EF4444]" title="Delete"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete All Bulk Lots Modal */}
      <DangerConfirmModal
        open={deleteAllOpen}
        onClose={() => !deleteAllLoading && setDeleteAllOpen(false)}
        onConfirm={async ({ confirmation_phrase, acknowledged }) => {
          setDeleteAllLoading(true);
          try {
            const res = await deleteAllBulkLots({ confirmation_phrase, acknowledged });
            toast.success(`Deleted ${res.data.deleted} bulk lots`);
            setDeleteAllOpen(false);
            fetchLots();
          } catch (err) {
            toast.error(err?.response?.data?.detail || 'Delete all failed');
          } finally { setDeleteAllLoading(false); }
        }}
        title="Delete All Bulk Lots"
        description={`This will permanently delete all ${counts.all} bulk lot(s) from the catalog. This action cannot be undone.`}
        confirmPhrase="DELETE ALL BULK LOTS"
        loading={deleteAllLoading}
      />

      {/* Quick Import Modal */}
      {quickImportOpen && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={resetQuickImport}>
          <div className="bg-[#0A0A0A] border border-[#F59E0B]/20 w-full max-w-3xl max-h-[85vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="quick-import-modal">
            <h3 className="text-lg font-bold mb-2 text-[#F59E0B]">Quick Import — Supplier Inventory</h3>
            <p className="text-xs text-white/40 mb-6">Upload your supplier's Excel file. All existing lots will be replaced.</p>

            {quickImportStep === 'upload' && (
              <div className="text-center py-12 border border-dashed border-white/10">
                <Upload className="w-10 h-10 mx-auto mb-4 text-white/20" />
                <p className="text-white/50 mb-4 text-sm">Drop your supplier Excel file here</p>
                <label className="inline-flex items-center gap-2 px-6 py-3 bg-[#F59E0B]/10 border border-[#F59E0B]/30 text-[#F59E0B] font-medium cursor-pointer hover:bg-[#F59E0B]/20 transition-colors">
                  <Upload className="w-4 h-4" />
                  {quickUploading ? 'Parsing...' : 'Select File'}
                  <input type="file" accept=".xlsx,.xls,.csv" onChange={handleQuickUpload} className="hidden" disabled={quickUploading} data-testid="quick-import-file" />
                </label>
              </div>
            )}

            {quickImportStep === 'preview' && quickImportData && (
              <>
                <div className="flex items-center gap-4 mb-4 p-3 bg-white/[0.02] border border-white/10">
                  <div>
                    <span className="text-xs text-white/40">File</span>
                    <p className="text-sm text-white font-mono">{quickImportData.filename}</p>
                  </div>
                  <div>
                    <span className="text-xs text-white/40">Lots detected</span>
                    <p className="text-xl font-bold text-[#F59E0B]">{quickImportData.total_lots}</p>
                  </div>
                </div>

                <div className="overflow-x-auto max-h-[45vh] border border-white/10">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-[#0A0A0A]">
                      <tr className="border-b border-white/10 text-white/50">
                        <th className="px-2 py-2 text-left">#</th>
                        <th className="px-2 py-2 text-left">LOT #</th>
                        <th className="px-2 py-2 text-left">TITLE</th>
                        <th className="px-2 py-2 text-left">BRAND</th>
                        <th className="px-2 py-2 text-left">CAT</th>
                        <th className="px-2 py-2 text-right">QTY</th>
                        <th className="px-2 py-2 text-left">GRADE</th>
                        <th className="px-2 py-2 text-right">COST</th>
                        <th className="px-2 py-2 text-right" data-testid="quick-import-sale-header">SALE PRICE</th>
                        <th className="px-2 py-2 text-right">AVG/UNIT</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quickImportData.lots.map((lot, i) => {
                        // Feb-2026 — SALE PRICE + AVG/UNIT come from the
                        // backend so preview matches saved / repaired
                        // pricing byte-for-byte.
                        const sale = lot.calculated_price ?? 0;
                        const avg = lot.avg_unit_price ?? 0;
                        return (
                          <tr key={i} className="border-b border-white/[0.04] hover:bg-white/[0.02]" data-testid={`quick-import-row-${lot.sku}`}>
                            <td className="px-2 py-1.5 text-white/30">{i + 1}</td>
                            <td className="px-2 py-1.5 text-[#00D4FF] font-mono">{lot.sku}</td>
                            <td className="px-2 py-1.5 text-white max-w-[250px] truncate">{lot.title}</td>
                            <td className="px-2 py-1.5 text-white/60">{lot.brand}</td>
                            <td className="px-2 py-1.5 text-white/60">{lot.category}</td>
                            <td className="px-2 py-1.5 text-right text-white/60 font-mono">{lot.quantity}</td>
                            <td className="px-2 py-1.5 text-white/50" data-testid={`quick-import-grade-${lot.sku}`}>{lot.grade}</td>
                            <td className="px-2 py-1.5 text-right text-white/50 font-mono">${lot.supplier_cost.toLocaleString()}</td>
                            <td className="px-2 py-1.5 text-right text-[#10B981] font-mono" data-testid={`quick-import-sale-${lot.sku}`}>${sale.toLocaleString()}</td>
                            <td className="px-2 py-1.5 text-right text-white/40 font-mono">${avg.toLocaleString()}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="flex items-center gap-3 mt-5 pt-4 border-t border-white/10">
                  <p className="text-xs text-red-400/60 flex-1">This will delete all {lots.length} existing lots and replace with {quickImportData.total_lots} new lots.</p>
                  <button onClick={resetQuickImport} className="px-4 py-2 text-sm text-white/60">Cancel</button>
                  <button onClick={handleQuickExecute} className="px-5 py-2.5 text-sm font-medium bg-[#F59E0B]/20 border border-[#F59E0B]/40 text-[#F59E0B] hover:bg-[#F59E0B]/30 transition-colors" data-testid="quick-import-execute">
                    Replace All & Import {quickImportData.total_lots} Lots
                  </button>
                </div>
              </>
            )}

            {quickImportStep === 'importing' && (
              <div className="text-center py-16">
                <RefreshCw className="w-8 h-8 mx-auto mb-4 text-[#F59E0B] animate-spin" />
                <p className="text-white/50">Importing lots...</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add Lot Modal */}
      {addingLot && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setAddingLot(false)}>
          <div className="bg-[#0A0A0A] border border-[#10B981]/20 w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="add-lot-modal">
            <h3 className="text-lg font-bold mb-6 text-[#10B981]">Add New Lot</h3>
            <div className="grid grid-cols-2 gap-4 mb-6">
              {[
                ['title', 'Title', 'col-span-2'], ['brand', 'Brand'], ['category', 'Category'],
                ['subcategory', 'Subcategory'], ['supplier_reference', 'Lot #'],
              ].map(([field, label, cls]) => (
                <div key={field} className={cls || ''}>
                  <label className="text-xs text-white/40 block mb-1">{label}{field === 'title' && <span className="text-red-400">*</span>}</label>
                  <input value={addForm[field] || ''} onChange={e => setAddForm({...addForm, [field]: e.target.value})} className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm" data-testid={`add-${field}`} />
                </div>
              ))}
              <div>
                <label className="text-xs text-white/40 block mb-1">Quantity</label>
                <input value={addForm.quantity || ''} onChange={e => recalcAdd({ quantity: e.target.value })} className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm" data-testid="add-quantity" />
              </div>
              <div>
                <label className="text-xs text-white/40 block mb-1">Grade</label>
                <input value={addForm.grade || ''} onChange={e => setAddForm({...addForm, grade: e.target.value})} className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm" data-testid="add-grade" />
              </div>
              <div>
                <label className="text-xs text-[#00D4FF]/60 block mb-1">Supplier Cost ($)</label>
                <input value={addForm.supplier_cost || ''} onChange={e => recalcAdd({ supplier_cost: e.target.value })} className="w-full px-3 py-2 bg-white/5 border border-[#00D4FF]/20 text-white text-sm" data-testid="add-supplier_cost" />
              </div>
              <div>
                <label className="text-xs text-[#00D4FF]/60 block mb-1">Markup %</label>
                <input value={addForm.markup_percent || ''} onChange={e => recalcAdd({ markup_percent: e.target.value })} className="w-full px-3 py-2 bg-white/5 border border-[#00D4FF]/20 text-white text-sm" data-testid="add-markup_percent" />
              </div>
              <div>
                <label className="text-xs text-white/40 block mb-1">Sale Price ($) <span className="text-[#00D4FF]/40 text-[10px]">auto</span></label>
                <input value={addForm.lot_price || ''} onChange={e => setAddForm({...addForm, lot_price: e.target.value})} className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm" data-testid="add-lot_price" />
              </div>
              <div>
                <label className="text-xs text-white/40 block mb-1">Avg Unit Price <span className="text-[#00D4FF]/40 text-[10px]">auto</span></label>
                <input value={addForm.avg_unit_price || ''} onChange={e => setAddForm({...addForm, avg_unit_price: e.target.value})} className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm" data-testid="add-avg_unit_price" />
              </div>
              {[
                ['cpu', 'CPU'], ['ram', 'RAM'],
                ['storage', 'Storage'], ['screen_size', 'Screen Size'],
              ].map(([field, label]) => (
                <div key={field}>
                  <label className="text-xs text-white/40 block mb-1">{label}</label>
                  <input value={addForm[field] || ''} onChange={e => setAddForm({...addForm, [field]: e.target.value})} className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm" data-testid={`add-${field}`} />
                </div>
              ))}
              <div className="col-span-2">
                <label className="text-xs text-white/40 block mb-1">Status</label>
                <select value={addForm.status || 'active'} onChange={e => setAddForm({...addForm, status: e.target.value})} className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm" data-testid="add-status">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="sold">Sold</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-xs text-white/40 block mb-1">Description</label>
                <textarea value={addForm.description || ''} onChange={e => setAddForm({...addForm, description: e.target.value})} rows={3} className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm resize-none" data-testid="add-description" />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-white/40 block mb-1">Condition Notes</label>
                <textarea value={addForm.condition_notes || ''} onChange={e => setAddForm({...addForm, condition_notes: e.target.value})} rows={2} className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm resize-none" data-testid="add-condition_notes" />
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setAddingLot(false)} className="px-4 py-2 text-sm text-white/60">Cancel</button>
              <button onClick={handleCreate} className="px-4 py-2 text-sm font-medium bg-[#10B981]/20 border border-[#10B981]/40 text-[#10B981]" data-testid="create-lot-btn">Create Lot</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingLot && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setEditingLot(null)}>
          <div className="bg-[#0A0A0A] border border-white/10 w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="edit-lot-modal">
            <h3 className="text-lg font-bold mb-6">Edit Lot</h3>
            <div className="grid grid-cols-2 gap-4 mb-6">
              {[
                ['title', 'Title', 'col-span-2'], ['brand', 'Brand'], ['category', 'Category'],
                ['subcategory', 'Subcategory'], ['supplier_reference', 'Lot #'],
              ].map(([field, label, cls]) => (
                <div key={field} className={cls || ''}>
                  <label className="text-xs text-white/40 block mb-1">{label}</label>
                  <input value={editForm[field] || ''} onChange={e => setEditForm({...editForm, [field]: e.target.value})} className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm" />
                </div>
              ))}
              {/* Quantity - triggers avg_unit_price recalc */}
              <div>
                <label className="text-xs text-white/40 block mb-1">Quantity</label>
                <input value={editForm.quantity || ''} onChange={e => recalcPricing({ quantity: e.target.value })} className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm" />
              </div>
              <div>
                <label className="text-xs text-white/40 block mb-1">Grade</label>
                <input value={editForm.grade || ''} onChange={e => setEditForm({...editForm, grade: e.target.value})} className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm" />
              </div>
              {/* Supplier Cost - triggers sale price + avg recalc */}
              <div>
                <label className="text-xs text-[#00D4FF]/60 block mb-1">Supplier Cost ($)</label>
                <input value={editForm.supplier_cost || ''} onChange={e => recalcPricing({ supplier_cost: e.target.value })} className="w-full px-3 py-2 bg-white/5 border border-[#00D4FF]/20 text-white text-sm" />
              </div>
              {/* Markup % - triggers sale price + avg recalc */}
              <div>
                <label className="text-xs text-[#00D4FF]/60 block mb-1">Markup %</label>
                <input value={editForm.markup_percent || ''} onChange={e => recalcPricing({ markup_percent: e.target.value })} className="w-full px-3 py-2 bg-white/5 border border-[#00D4FF]/20 text-white text-sm" />
              </div>
              {/* Sale Price - auto-calculated but manually overridable */}
              <div>
                <label className="text-xs text-white/40 block mb-1">Sale Price ($) <span className="text-[#00D4FF]/40 text-[10px]">auto</span></label>
                <input value={editForm.lot_price || ''} onChange={e => setEditForm({...editForm, lot_price: e.target.value})} className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm" />
              </div>
              {/* Avg Unit Price - auto-calculated but manually overridable */}
              <div>
                <label className="text-xs text-white/40 block mb-1">Avg Unit Price <span className="text-[#00D4FF]/40 text-[10px]">auto</span></label>
                <input value={editForm.avg_unit_price || ''} onChange={e => setEditForm({...editForm, avg_unit_price: e.target.value})} className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm" />
              </div>
              {[
                ['cpu', 'CPU'], ['ram', 'RAM'],
                ['storage', 'Storage'], ['screen_size', 'Screen Size'],
              ].map(([field, label]) => (
                <div key={field}>
                  <label className="text-xs text-white/40 block mb-1">{label}</label>
                  <input value={editForm[field] || ''} onChange={e => setEditForm({...editForm, [field]: e.target.value})} className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm" />
                </div>
              ))}
              <div className="col-span-2">
                <label className="text-xs text-white/40 block mb-1">Status</label>
                <select value={editForm.status || 'active'} onChange={e => setEditForm({...editForm, status: e.target.value})} className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="sold">Sold</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-xs text-white/40 block mb-1">Description</label>
                <textarea value={editForm.description || ''} onChange={e => setEditForm({...editForm, description: e.target.value})} rows={3} className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm resize-none" />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-white/40 block mb-1">Condition Notes</label>
                <textarea value={editForm.condition_notes || ''} onChange={e => setEditForm({...editForm, condition_notes: e.target.value})} rows={2} className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm resize-none" />
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setEditingLot(null)} className="px-4 py-2 text-sm text-white/60">Cancel</button>
              <button onClick={saveEdit} className="px-4 py-2 text-sm font-medium bg-[#00D4FF]/20 border border-[#00D4FF]/40 text-[#00D4FF]" data-testid="save-lot-btn">Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
